import {
  REEBELO_CURRENCY,
  REEBELO_DOMAINS,
  ReebeloConditions,
  ReebeloCurrencies,
} from '@lambda/reebelo';
import { CheckoutT, CustomerOrderT } from '@lambda/apis/src/checkout/types';
import { CartT } from '@lambda/apis/src/cart/types';
import {
  PriceComponentName,
  PriceComponentsT,
} from '@lambda/apis/src/catalog-v2/types';
import {
  CheckoutEventProperties,
  CheckoutTrackRequest,
  ProductEventProperties,
} from './types';
import { makeProductProperties } from './trackProduct';
import settings from '@/settings';
import { logger } from '../logger';
import {
  getAnalytics,
  shouldTrack,
  trackEvent,
} from './vendor/segment/segment';
import { SegmentEvent } from './events';
import { OffersT } from '../checkout/types';
import { getUniqueDiscountCodes } from '../checkout/helpers';
import {
  getAdjustmentFactor,
  getBuyerFeeAdjustmentFactor,
  getLineItemCategory,
} from './adjustmentFactors';

const getBuyerFee = (priceComponents?: PriceComponentsT[]): number =>
  (priceComponents?.find(
    (c) => c.name === PriceComponentName.BUYER_PROTECTION_FEE,
  )?.price ?? 0) / 100;

// Builds the `adjusted_value` conversion-tracking properties for the
// Checkout Completed event only (RT-16227) — kept out of `makeFromOrder`/
// `makeFromCheckout` so the other checkout events don't pick up these fields.
// The event's existing fields (built from `order.lineItems`) are left as-is;
// `checkout.lineItems` is only consulted here for category/buyer-fee data
// that `order.lineItems` doesn't carry. `variantId` is never populated on
// either side, so lines are matched up via checkout line `typeId`, which
// mirrors the order line's `offerId` for both PRODUCT and WARRANTY lines.
export const makeAdjustedValueProperties = (
  checkout: CheckoutT,
  order: CustomerOrderT,
): Pick<CheckoutEventProperties, 'products' | 'adjusted_value'> => {
  const checkoutLinesByOfferId = new Map(
    checkout.lineItems.map((line) => [line.typeId, line]),
  );

  const products = order.lineItems.map((e) => {
    const checkoutLine = checkoutLinesByOfferId.get(e.offerId);
    const price = e.price / 100;
    const buyer_fee = getBuyerFee(checkoutLine?.priceComponents);
    const product_type = getLineItemCategory(
      e.type,
      checkoutLine?.categoryName,
    );
    const adjustment_factor = getAdjustmentFactor(
      settings.store,
      e.type,
      checkoutLine?.categoryName,
    );
    const buyer_fee_adjustment_factor = getBuyerFeeAdjustmentFactor(
      settings.store,
    );
    const adjusted_value =
      e.quantity *
      ((price - buyer_fee) * adjustment_factor +
        buyer_fee * buyer_fee_adjustment_factor);

    return {
      variant_id: String(e.variantId),
      name: e.title,
      price,
      vendor: e.vendorId,
      currency: order.currency as ReebeloCurrencies,
      quantity: e.quantity,
      store: settings.store,
      product_type,
      buyer_fee,
      adjustment_factor,
      buyer_fee_adjustment_factor,
      adjusted_value,
    };
  });

  return {
    products,
    adjusted_value: products.reduce((sum, p) => sum + p.adjusted_value, 0),
  };
};

const makeFromThankYou = (
  checkout: CheckoutT,
  order: CustomerOrderT,
  offers: OffersT,
): CheckoutEventProperties => {
  const value = Number(order.grandTotal || 0) / 100;
  const discount = Number(order.discountTotal || 0) / 100;
  const coupons = getUniqueDiscountCodes(checkout).map((c) => c.code);

  const products: ProductEventProperties[] = order.lineItems.map((e) => {
    const key = `${e.variantId}`;
    const offer = offers[key];

    return {
      variant_id: String(e.variantId),
      psku: offer?.psku,
      condition: offer?.condition as ReebeloConditions,
      category: offer?.categories?.length ? offer.categories[0] : undefined,
      brand: offer?.brand,
      color: offer?.color,
      name: e.title,
      price: e.price / 100,
      vendor: e.vendorId, // Vendor not provided
      currency: order.currency as ReebeloCurrencies,
      quantity: e.quantity,
      store: settings.store,
      offer_pk: offer?.reebeloPk,
    };
  });

  const phone = order.shippingAddress?.phone || order.billingAddress?.phone;

  return {
    store: settings.store,
    order_id: String(order.id),
    checkout_id: checkout.id,
    email: order.email,
    phone,
    discount,
    coupon: coupons.join(', '),
    value,
    currency: order.currency as ReebeloCurrencies,
    products,
  };
};

const makeFromCart = (cart: CartT): CheckoutEventProperties => {
  const products = cart.lines.map((e) =>
    makeProductProperties({
      cartLine: e,
    }),
  );

  return {
    store: settings.store,
    order_id: cart.id,
    currency: REEBELO_CURRENCY[settings.store],
    products,
  };
};

export const makeFromCheckout = (
  checkout: CheckoutT,
): CheckoutEventProperties => {
  const discount =
    (checkout?.discounts?.reduce((acc, d) => acc + d.amount, 0) || 0) / 100;

  const value = checkout.grandTotal / 100;

  const coupon = checkout?.discounts?.map((d) => d.code).join(', ');

  const phone =
    checkout.billingAddress?.phone || checkout.shippingAddress?.phone;

  const products: ProductEventProperties[] = checkout.lineItems.map((e) => ({
    offer_id: e.offerId,
    variant_id: e.variantId,
    sku: e.sku,
    psku: e.psku,
    name: e.title,
    category: e.categoryName,
    price: (e.price ?? 0) / 100,
    compare_price: (e.compareAtPrice ?? 0) / 100,
    currency: checkout.currency as ReebeloCurrencies,
    quantity: e.quantity,
    images: [e.image ?? ''],
    store: checkout.shopifyStore,
    vendor: e.vendorId?.split('#')[1],
  }));

  return {
    store: settings.store,
    checkout_id: checkout.id,
    order_id: checkout.orderId ?? checkout.shopifyOrderId,
    email: checkout.email,
    phone,
    discount,
    coupon,
    value,
    tax: checkout.taxTotal / 100,
    duties: checkout.dutiesTotal / 100,
    currency: checkout.currency as ReebeloCurrencies,
    products,
  };
};

const makeFromOrder = (order: CustomerOrderT): CheckoutEventProperties => {
  const value = Number(order.grandTotal || 0) / 100;
  const discount = Number(order.discountTotal || 0) / 100;
  const coupon = order.discounts?.map((d) => d.code).join(', ');

  const products: ProductEventProperties[] = order.lineItems.map((e) => ({
    variant_id: String(e.variantId),
    name: e.title,
    price: e.price / 100,
    vendor: e.vendorId, // Vendor not provided
    currency: order.currency as ReebeloCurrencies,
    quantity: e.quantity,
    store: settings.store,
  }));

  const phone = order.shippingAddress?.phone || order.billingAddress?.phone;

  return {
    store: settings.store,
    order_id: String(order.id),
    checkout_id: order.checkoutId,
    email: order.email,
    phone,
    discount,
    coupon,
    value,
    currency: order.currency as ReebeloCurrencies,
    products,
  };
};

const makeCheckoutProperties = (
  r: CheckoutTrackRequest,
): CheckoutEventProperties => {
  const { cart, checkout, order, offers } = r;

  if (checkout && order && offers) {
    return makeFromThankYou(checkout, order, offers);
  }

  if (cart && checkout) {
    return { ...makeFromCart(cart), ...makeFromCheckout(checkout) };
  }

  if (order) {
    return makeFromOrder(order);
  }

  if (cart) {
    return makeFromCart(cart);
  }

  if (checkout) {
    return makeFromCheckout(checkout);
  }

  return {};
};

const trackCheckoutEvent = (
  event: SegmentEvent,
  r: CheckoutTrackRequest,
  extraProperties?: Partial<CheckoutEventProperties>,
): void => {
  try {
    logger.info({ event, r }, 'tracking checkout event');

    const properties = {
      ...makeCheckoutProperties(r),
      ...extraProperties,
      ...r.properties,
    };

    trackEvent(event, properties);
  } catch (e: any) {
    logger.error({ event, r }, 'error tracking checkout event');
  }
};

export const trackCheckoutStarted = (r: CheckoutTrackRequest) =>
  trackCheckoutEvent(SegmentEvent.CHECKOUT_STARTED, r);

export const trackCheckoutFailed = (r: CheckoutTrackRequest) =>
  trackCheckoutEvent(SegmentEvent.CHECKOUT_PAYMENT_FAILED, r);

export const trackContactEmailEntered = (r: CheckoutTrackRequest) =>
  trackCheckoutEvent(SegmentEvent.CHECKOUT_CONTACT_EMAIL_ENTERED, r);

export const trackShippingInfoEntered = (r: CheckoutTrackRequest) =>
  trackCheckoutEvent(SegmentEvent.CHECKOUT_SHIPPING_INFO_ENTERED, r);

export const trackShippingInfoFieldEngaged = (r: CheckoutTrackRequest) =>
  trackCheckoutEvent(SegmentEvent.CHECKOUT_SHIPPING_INFO_FIELD_ENGAGED, r);

export const trackShippingInfoFieldUpdated = (r: CheckoutTrackRequest) =>
  trackCheckoutEvent(SegmentEvent.CHECKOUT_SHIPPING_INFO_FIELD_UPDATED, r);

export const trackCheckoutTaxUpdated = (r: CheckoutTrackRequest) =>
  trackCheckoutEvent(SegmentEvent.CHECKOUT_TAX_UPDATED, r);

export const trackPaymentInfoEntered = (r: CheckoutTrackRequest) =>
  trackCheckoutEvent(SegmentEvent.CHECKOUT_PAYMENT_INFO_ENTERED, r);

export const trackPaymentSubmitted = (r: CheckoutTrackRequest) =>
  trackCheckoutEvent(SegmentEvent.CHECKOUT_PAYMENT_SUBMITTED, r);

export const trackCheckoutCompleted = (r: CheckoutTrackRequest) =>
  trackCheckoutEvent(
    SegmentEvent.CHECKOUT_COMPLETED,
    r,
    r.checkout && r.order
      ? makeAdjustedValueProperties(r.checkout, r.order)
      : undefined,
  );

export const trackDiscountApplied = (r: CheckoutTrackRequest) =>
  trackCheckoutEvent(SegmentEvent.CHECKOUT_DISCOUNT_APPLIED, r);

export const trackDiscountDenied = (r: CheckoutTrackRequest) =>
  trackCheckoutEvent(SegmentEvent.CHECKOUT_DISCOUNT_DENIED, r);

export const trackDiscountRemoved = (r: CheckoutTrackRequest) =>
  trackCheckoutEvent(SegmentEvent.CHECKOUT_DISCOUNT_REMOVED, r);

export const trackAbandonedCheckout = (checkout: CheckoutT) => {
  try {
    logger.info({ checkout }, 'tracking abandoned checkout');

    const domain = REEBELO_DOMAINS[checkout?.shopifyStore];
    const url = `https://${domain}/checkout/${checkout.id}`;

    if (!shouldTrack()) {
      logger.info({ checkout }, 'not tracking abandoned checkout');

      return;
    }

    const { email } = checkout;

    getAnalytics()?.identify({
      email,
      abandoned_checkout_url: url,
    });
  } catch (e: any) {
    logger.error({ checkout }, 'error tracking abandoned checkout');
  }
};

export const clearAbandonedCheckout = () => {
  getAnalytics()?.identify({ abandoned_checkout_url: null });
};
